<template>
    <div class="sembonus-selection" v-if="hasBonus">
        <Element font="font-body-m-bold" color="neutral-70">
            {{ t({ key: 'sembonus.you_have_bonus_nights_to_redeem' }) }}
        </Element>

        <div v-for="(item, index) in bonus" :key="index">
            <Checkbox v-model="item.IsApplied" :disabled="isUpdating" density="compact">
                {{
                    t({
                        key: 'sembonus.bonus_night_redeem',
                        values: [getMoneyObjectAsString(item.Value)],
                    })
                }}
            </Checkbox>
        </div>

        <Element class="sembonus-selection-error" v-if="error" color="red">
            {{ t({ key: 'sembonus.error_bonus_night_not_applied' }) }}
        </Element>
    </div>
</template>

<script lang="ts">
    import { computed, ref, defineComponent, onMounted, watch } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { useStore } from '@/store';
    import { getMoneyObjectAsString } from '@shared/helpers/money';
    import { Money } from '@libs/money/money.interfaces';
    import { endpoints } from '@/api/endpoints';
    import { Bonus, Cart } from '@/api/Cart/cart.interfaces';
    import { sumBy } from 'lodash';
    import { getErrorMessage, handleCustomError } from '@/config/errorHandling';
    import { SembonusView } from '@/tracking/statsig/types';
    import {
        logStatsigCheckoutSembonusApplied,
        logStatsigCheckoutSembonusAvailable,
        logStatsigCheckoutSembonusView,
    } from '@/tracking/statsig/checkout.functions';
    import Element from '@ui/components/Element/Element.vue';
    import Checkbox from '@ui/components/FormV2/Fields/Checkbox/Checkbox.vue';

    export default defineComponent({
        name: 'Selection',
        methods: { t, getMoneyObjectAsString },
        components: { Element, Checkbox },
        setup() {
            const store = useStore();

            const prevSelectedBonus = ref<Bonus[]>([]);
            const error = ref<boolean>(false);

            const isUpdating = computed<boolean>(
                () => store.state.cart.status === 'UPDATING'
            );

            const cart = computed<Cart>(() => store.state.cart.data);
            const bonus = computed<Bonus[]>(() => store.getters['cart/getAvailableBonus']);
            const hasBonus = computed<boolean>(() => bonus.value.length > 0);

            const selectedBonus = computed<Money[]>(() => {
                return bonus.value.filter((n) => n.IsApplied).map((n) => n.Value);
            });

            const totalBonus = computed(() => ({
                Amount: sumBy(selectedBonus.value, 'Amount'),
                Currency: selectedBonus.value[0]?.Currency || '',
            }));

            onMounted(() => initTracking());

            const initTracking = () => {
                if (!bonus.value.length) {
                    logStatsigCheckoutSembonusView(
                        SembonusView.RedeemNoneAvailable,
                        cart.value
                    );
                    return;
                } else {
                    logStatsigCheckoutSembonusView(
                        SembonusView.RedeemBonusNights,
                        cart.value
                    );
                    logStatsigCheckoutSembonusAvailable(
                        bonus.value.length,
                        {
                            Amount: sumBy(bonus.value, 'Value.Amount'),
                            Currency: bonus.value[0].Value.Currency,
                        },
                        cart.value
                    );
                }
            };

            const rollbackSelection = () => {
                error.value = true;

                const rollbackCart = { ...cart.value };

                rollbackCart.AvailableBonus = rollbackCart.AvailableBonus.map((b, index) => ({
                    ...b,
                    IsApplied: prevSelectedBonus.value[index]?.IsApplied ?? false,
                }));

                store.dispatch('cart/setCartSuccess', rollbackCart);
            };

            const onSelection = async () => {
                try {
                    error.value = false;

                    store.dispatch('cart/setCartUpdating');
                    const amounts = selectedBonus.value.map((x) => x.Amount);

                    const updatedCart = await endpoints.sembonus.UpdateAppliedBonus(
                        amounts,
                        cart.value
                    );

                    logStatsigCheckoutSembonusApplied(
                        selectedBonus.value.length,
                        totalBonus.value,
                        cart.value
                    );

                    const chosenAddons = cart.value.Products.filter(
                        (p) => p.Type === 'Addon'
                    );

                    const availableAddonsMap = new Map(
                        updatedCart.AvailableAddons.map((a) => [a.$type, a])
                    );

                    chosenAddons.forEach((addon) => {
                        const match = availableAddonsMap.get(addon.$type);
                        if (match) updatedCart.Products.push(match);
                    });

                    store.dispatch('cart/setCartSuccess', updatedCart);

                    prevSelectedBonus.value = bonus.value;
                } catch (error) {
                    rollbackSelection();
                    handleCustomError(
                        `[Sembonus] onSelection UpdateAppliedBonus ${getErrorMessage(error)}`
                    );
                } finally {
                    store.dispatch('cart/resetCartUpdating');
                }
            };

            watch(
                () => bonus.value.map((b) => b.IsApplied).join(','),
                () => onSelection()
            );

            return {
                hasBonus,
                bonus,
                isUpdating,
                error,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'Selection';
</style>
